import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-apollo';
import { FormattedMessage } from 'react-intl';
import { TransactionStatusIcon } from '../../../components/TransactionStatusIcon';
import { ReactComponent as HokRefreshIcon } from '../../../img/icons/hok-refresh.svg';
import {
  TRANSACTION_NEW_POLICY,
  TRANSACTION_STATUS_WAITING,
  TRANSACTION_STATUS_COMPLETED,
  TRANSACTION_STATUS_PAID,
  TRANSACTION_STATUS_FAILED,
} from '../../../constants/userTransactions';
import {
  AdminRebuildPolicyMutation,
  AdminResendPolicyEmailMutation,
  AdminResendTransactionInvoiceMutation,
} from '../../../operations/mutations/AdminUserTransactionMutation';
import { handleRequestError } from '../../../constants/errorCodes';
import { UserInteractionModal } from '../../../components/UserInteractionModal';
import { CustomButton } from '../../../containers';
import { Row, Col } from 'reactstrap';
import { renderToaster, SUCCESS_TOASTER } from '../../../constants/toaster';

const TransactionManagementStatusBox = React.memo(
  ({ status, statusName, refreshEnabled = true, onRefresh = null, loading = false, children }) => {
    return (
      <div className="hok-user-transaction-status-box">
        <div className="hok-user-transaction-status-header">
          <h4>
            <FormattedMessage id={`adminUserTransactionPreviewPage.${statusName}`} />
          </h4>
          {typeof onRefresh === 'function' && (
            <div
              className="hok-user-transaction-refresh"
              onClick={typeof onRefresh === 'function' && refreshEnabled ? onRefresh : () => null}>
              <HokRefreshIcon
                className={
                  typeof onRefresh === 'function' && refreshEnabled ? `svg-red` : `svg-gray${loading ? ' active' : ''}`
                }
                height="32"
                width="32"
              />
            </div>
          )}
        </div>
        <div className="hok-user-transaction-status-content">
          <div className="hok-user-transaction-status-icon">
            <TransactionStatusIcon status={status} height="70" width="70" />
          </div>
          <div className="hok-user-transaction-status-description">
            <h5>
              <FormattedMessage
                id={`adminUserTransactionsPage.${statusName}Filter.${status || TRANSACTION_STATUS_WAITING}`}
              />
            </h5>
            <p>
              <span className="text-uppercase font-weight-bold">
                <FormattedMessage id="description" />:
              </span>{' '}
              {children}
            </p>
          </div>
        </div>
      </div>
    );
  }
);

export const TransactionManagement = React.memo(({ transaction, fetchMore }) => {
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const toggle = () => {
    setIsOpen(open => !open);
  };

  const [isErrorDescOpen, setIsErrorDescOpen] = useState(false);
  const [errorDescContent, setErrorDescContent] = useState(null);

  const toggleErrorDesc = () => {
    setIsErrorDescOpen(open => !open);
  };

  const onCompleted = () => {
    renderToaster(`successMessages.userTransactionProcessRestarted`, SUCCESS_TOASTER, { intlKey: true });
    fetchMore({
      variables: { filter: { confirmationCode: transaction.confirmationCode } },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          getPayments: {
            ...fetchMoreResult.getPayments,
          },
        };
      },
    });
  };

  const shortenErrorDescription = message => {
    return message.length > 130 ? `${message.substring(0, 130).trim()}...` : message;
  };

  const [rebuildPolicy, { loading: rebuildLoading }] = useMutation(AdminRebuildPolicyMutation, {
    variables: { paymentId: transaction.confirmationCode, force: true },
    onError: error => handleRequestError(error, dispatch),
    onCompleted,
  });

  const [resendEmail, { loading: resendEmailLoading }] = useMutation(AdminResendPolicyEmailMutation, {
    variables: { paymentId: transaction.confirmationCode },
    onError: error => handleRequestError(error, dispatch),
    onCompleted,
  });

  const [resendInvoice, { loading: resendInvoiceLoading }] = useMutation(AdminResendTransactionInvoiceMutation, {
    variables: { paymentId: transaction.confirmationCode },
    onError: error => handleRequestError(error, dispatch),
    onCompleted,
  });

  const thirdPartyAPIBuyResponse = useMemo(() => {
    try {
      if (transaction.thirdPartyAPIBuyResponse) {
        return JSON.parse(transaction.thirdPartyAPIBuyResponse);
      }
      return '';
    } catch (e) {
      return '';
    }
  }, [transaction.thirdPartyAPIBuyResponse]);

  const thirdPartyAPIInvoiceResponse = useMemo(() => {
    try {
      if (transaction.thirdPartyAPIInvoiceResponse) {
        return JSON.parse(transaction.thirdPartyAPIInvoiceResponse);
      }
      return '';
    } catch (e) {
      return '';
    }
  }, [transaction.thirdPartyAPIInvoiceResponse]);

  const processName = useMemo(() => {
    switch (modalContent) {
      case 'processStatus':
        return {
          message: <FormattedMessage id="adminUserTransactionPreviewPage.warning.processStatus" />,
          refreshFn: rebuildPolicy,
        };
      case 'emailStatus':
        return {
          message: <FormattedMessage id="adminUserTransactionPreviewPage.warning.processStatus" />,
          refreshFn: resendEmail,
        };
      case 'invoiceReceivedStatus':
        return {
          message: <FormattedMessage id="adminUserTransactionPreviewPage.warning.processStatus" />,
          refreshFn: resendInvoice,
        };
      default:
        return { message: '', refreshFn: () => null };
    }
  }, [modalContent, rebuildPolicy, resendEmail, resendInvoice]);

  return (
    <div className="hok-user-transaction-mngmt mb-5 mb-md-0">
      <TransactionManagementStatusBox status={transaction.status} statusName="status" refresh={false}>
        <FormattedMessage
          id={`adminUserTransactionPreviewPage.statusMessage.status.${transaction.status ||
            TRANSACTION_STATUS_WAITING}`}
        />
      </TransactionManagementStatusBox>
      {transaction.type === TRANSACTION_NEW_POLICY && (
        <>
          <TransactionManagementStatusBox
            status={transaction.processStatus}
            statusName="processStatus"
            onRefresh={() => {
              setModalContent('processStatus');
              toggle();
            }}
            loading={rebuildLoading}
            refreshEnabled={
              transaction.processStatus &&
              transaction.processStatus === TRANSACTION_STATUS_FAILED &&
              transaction.status === TRANSACTION_STATUS_PAID &&
              !rebuildLoading
            }>
            {transaction.processStatus === TRANSACTION_STATUS_FAILED && thirdPartyAPIBuyResponse ? (
              <span>
                {thirdPartyAPIBuyResponse.message ? (
                  <>
                    {shortenErrorDescription(thirdPartyAPIBuyResponse.message)}{' '}
                    <span
                      className="hok-back-link text-capitalize"
                      onClick={() => {
                        setErrorDescContent(thirdPartyAPIBuyResponse);
                        toggleErrorDesc();
                      }}>
                      <FormattedMessage id="more" />
                    </span>
                  </>
                ) : (
                  <FormattedMessage
                    id={`adminUserTransactionPreviewPage.statusMessage.processStatus.${TRANSACTION_STATUS_FAILED}`}
                  />
                )}
              </span>
            ) : (
              <FormattedMessage
                id={`adminUserTransactionPreviewPage.statusMessage.processStatus.${transaction.processStatus ||
                  TRANSACTION_STATUS_WAITING}`}
              />
            )}
          </TransactionManagementStatusBox>
          <TransactionManagementStatusBox
            status={transaction.emailStatus}
            statusName="emailStatus"
            onRefresh={() => {
              setModalContent('emailStatus');
              toggle();
            }}
            loading={resendEmailLoading}
            refreshEnabled={
              transaction.emailStatus &&
              transaction.emailStatus === TRANSACTION_STATUS_FAILED &&
              transaction.processStatus === TRANSACTION_STATUS_COMPLETED &&
              !rebuildLoading &&
              !resendEmailLoading
            }>
            {transaction.emailStatus === TRANSACTION_STATUS_FAILED && transaction.emailId && (
              <>
                <span>Sendgrid ID: {transaction.emailId}.</span>{' '}
              </>
            )}
            <FormattedMessage
              id={`adminUserTransactionPreviewPage.statusMessage.emailStatus.${transaction.emailStatus ||
                TRANSACTION_STATUS_WAITING}`}
            />
          </TransactionManagementStatusBox>
        </>
      )}
      <TransactionManagementStatusBox
        status={transaction.invoiceReceivedStatus}
        statusName="invoiceReceivedStatus"
        onRefresh={() => {
          setModalContent('invoiceReceivedStatus');
          toggle();
        }}
        loading={resendInvoiceLoading}
        refreshEnabled={
          transaction.invoiceReceivedStatus &&
          transaction.invoiceReceivedStatus === TRANSACTION_STATUS_FAILED &&
          transaction.emailStatus === TRANSACTION_STATUS_COMPLETED &&
          !rebuildLoading &&
          !resendEmailLoading &&
          !resendInvoiceLoading
        }>
        {transaction.invoiceReceivedStatus === TRANSACTION_STATUS_FAILED ? (
          <span>
            {thirdPartyAPIInvoiceResponse.message ? (
              <>
                {shortenErrorDescription(thirdPartyAPIInvoiceResponse.message)}{' '}
                <span
                  className="hok-back-link text-capitalize"
                  onClick={() => {
                    setErrorDescContent(thirdPartyAPIInvoiceResponse);
                    toggleErrorDesc();
                  }}>
                  <FormattedMessage id="more" />
                </span>
              </>
            ) : (
              <FormattedMessage
                id={`adminUserTransactionPreviewPage.statusMessage.invoiceReceivedStatus.${TRANSACTION_STATUS_FAILED}`}
              />
            )}
          </span>
        ) : (
          <FormattedMessage
            id={`adminUserTransactionPreviewPage.statusMessage.invoiceReceivedStatus.${transaction.invoiceReceivedStatus ||
              TRANSACTION_STATUS_WAITING}`}
          />
        )}
      </TransactionManagementStatusBox>
      <UserInteractionModal
        isOpen={isOpen}
        toggle={toggle}
        titleId="modalHeaders.warning"
        textComponent={
          <Row className="no-gutters">
            <Col sm={{ size: 10, offset: 1 }}>
              <p className="mb-0 text-center">{processName.message}</p>
            </Col>
          </Row>
        }>
        <CustomButton
          type="button"
          outline
          className="hok-outline-btn d-block d-sm-inline-block mx-0 mr-sm-1 w-xs-100"
          block={false}
          translationId="back"
          onClick={() => {
            setIsOpen(false);
          }}
        />
        <CustomButton
          className="hok-dashboard-btn d-block d-sm-inline-block mx-0 ml-sm-1 mt-2 mt-sm-0 w-xs-100"
          block={false}
          translationId="ok"
          onClick={() => {
            toggle();
            processName.refreshFn();
          }}
        />
      </UserInteractionModal>
      <UserInteractionModal
        isOpen={isErrorDescOpen}
        toggle={toggleErrorDesc}
        titleId="modalHeaders.errorDescription"
        textComponent={
          <Row className="no-gutters">
            <Col xs="12">
              <div className="hok-user-transactions-box hok-error-description">
                {JSON.stringify(errorDescContent, null, 2)}
              </div>
            </Col>
          </Row>
        }
      />
    </div>
  );
});
